<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="element-box">
        <CoachingOverviewData
            :coaching="coachingData"
            :calculatedUnits="calculatedUnitsData"/>

        <Menu :coaching="coachingData"
              :currentUser="piniaCurrentUser"/>

        <form v-if="isLoaded" @submit.prevent="submitForm" novalidate class="mt-4">
          <div class="row mt-3">

            <!-- BUDGET PROOF -->
            <div class="col-12 col-md-5 mb-3">
              <div class="row align-items-center">
                <div class="col-6">
                  <BaseCheckbox
                      v-model="form.budgetProofUploaded"
                      label="Budgetnachweis hochgeladen"
                      :classFormGroup="'mb-0'"
                      :labelPosition="'right'"/>
                </div>
                <div class="col-4">
                  <Datepicker
                      v-model="form.budgetProofUploadedAt"
                      :disabled="!form.budgetProofUploaded"
                      position="down"
                      :required="true"
                      :validation="v$?.budgetProofUploadedAt"
                      :messages="v$?.budgetProofUploadedAt?.required?.$message"/>
                </div>
              </div>
            </div>

            <!-- CALCULATION -->
            <div class="col-12 col-md-5 mb-3">
              <div class="row align-items-center">
                <div class="col-6">
                  <BaseCheckbox
                      v-model="form.calculationCreated"
                      label="Berechnung erstellt"
                      :classFormGroup="'mb-0'"
                      :labelPosition="'right'"/>
                </div>
                <div class="col-4">
                  <Datepicker
                      v-model="form.calculationCreatedAt"
                      :disabled="!form.calculationCreated"
                      position="down"
                      :validation="v$?.calculationCreatedAt"
                      :messages="v$?.calculationCreatedAt?.required?.$message"/>
                </div>
              </div>
            </div>

            <!-- BILLING RECEIVED -->
            <div class="col-12 col-md-5 mb-3">
              <div class="row align-items-center">
                <div class="col-6">
                  <BaseCheckbox
                      v-model="form.billingReceived"
                      label="Abrechnung eingegangen"
                      :classFormGroup="'mb-0'"
                      :labelPosition="'right'"/>
                </div>
                <div class="col-4">
                  <Datepicker
                      v-model="form.billingReceivedAt"
                      :disabled="!form.billingReceived"
                      position="down"
                      :validation="v$?.billingReceivedAt"
                      :messages="v$?.billingReceivedAt?.required?.$message"/>
                </div>
              </div>
            </div>

            <!-- BILLING COMPLETED -->
            <div class="col-12 col-md-5 mb-3">
              <div class="row align-items-center">
                <div class="col-6">
                  <BaseCheckbox
                      v-model="form.billingCompleted"
                      label="Abrechnung abgeschlossen"
                      :classFormGroup="'mb-0'"
                      :labelPosition="'right'"/>
                </div>
                <div class="col-4">
                  <Datepicker
                      v-model="form.billingCompletedAt"
                      :disabled="!form.billingCompleted"
                      position="down"
                      :validation="v$?.billingCompletedAt"
                      :messages="v$?.billingCompletedAt?.required?.$message"/>
                </div>
              </div>
            </div>

          </div>
          <div class="d-flex justify-content-center w-100 mt-2">
            <SaveButton
                :label="$t('general.button.save')"
                :loading="isSubmitting"/>
          </div>
        </form>

      </div>
    </div>
  </PageTemplate>
  <div></div>
</template>

<script setup>
import BaseCheckbox from '@/components/base/BaseCheckbox.vue';
import Loader from '@/components/Loader.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Datepicker from '@/components/picker/Datepicker.vue';
import {piniaUserStore} from '@/store/piniaUserStore';
import CoachingOverviewData from '@/views/App/Portal/AssistedLivingCoaching/CoachingOverviewData.vue';
import Menu from '@/views/App/Portal/AssistedLivingCoaching/Menu.vue';
import {computed, onMounted, reactive, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRoute} from 'vue-router';
import {CloseToast, ErrorToast, SuccessToast} from "@/components/VueToast";
import coachingService from "@/api/Unemployed/AssistedLivingCoaching";
import {requiredRule} from "@/shared/validators/validationRules";
import useVuelidate from "@vuelidate/core";
import portalService from "@/api/Portal/AssistedLivingCoaching";
import {downloadFile} from "@/shared/functions";
import SaveButton from '@/components/button/SaveButton.vue';

const piniaUserState = piniaUserStore();
const piniaCurrentUser = computed(() => piniaUserState.currentUser.user);
const {t} = useI18n();
const route = useRoute();
const coachingData = ref('');
const isLoaded = ref(false);
const isSubmitting = ref(false);
const calculatedUnitsData = ref('');
const coachingId = +route.params.id;

// form state
const form = reactive({
  budgetProofUploaded: false,
  budgetProofUploadedAt: null,

  calculationCreated: false,
  calculationCreatedAt: null,

  billingReceived: false,
  billingReceivedAt: null,

  billingCompleted: false,
  billingCompletedAt: null
})

const formRules = ref({});

const computedRules = computed(() => formRules.value);
const v$ = useVuelidate(computedRules, form);


watch(() => form.budgetProofUploaded, (val) => {
  if (!val) {
    form.budgetProofUploadedAt = null
    formRules.value = {...formRules.value, budgetProofUploadedAt: ""};
  } else {
    formRules.value = {...formRules.value, budgetProofUploadedAt: requiredRule(t)};
  }
})

watch(() => form.calculationCreated, (val) => {
  if (!val) {
    form.calculationCreatedAt = null
    formRules.value = {...formRules.value, calculationCreatedAt: ""};
  } else {
    formRules.value = {...formRules.value, calculationCreatedAt: requiredRule(t)};
  }
})

watch(() => form.billingReceived, (val) => {
  if (!val) {
    form.billingReceivedAt = null
    formRules.value = {...formRules.value, billingReceivedAt: ""};
  } else {
    formRules.value = {...formRules.value, billingReceivedAt: requiredRule(t)};
  }
})

watch(() => form.billingCompleted, (val) => {
  if (!val) {
    form.billingCompletedAt = null
    formRules.value = {...formRules.value, billingCompletedAt: ""};
  } else {
    formRules.value = {...formRules.value, billingCompletedAt: requiredRule(t)};
  }
})


async function geCoachingData() {
  try {
    CloseToast();
    const { data } = await coachingService.getAssistedLivingCoaching(+coachingId);

    coachingData.value = data.data;
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getDocumentsData() {
  try {
    const { data } = await portalService.getIntroductoryDocuments(+coachingId);
    fillForm(data.data)

    const { data: calculatedUnits } = await coachingService.getCalculatedUnits(+coachingId);
    calculatedUnitsData.value       = calculatedUnits.data;
  } catch (error) {
    await ErrorToast(error);
  }
}

function fillForm(data) {
  form.budgetProofUploaded = data.budgetProofUploaded ?? false
  form.budgetProofUploadedAt = data.budgetProofUploadedAt ?? null

  form.calculationCreated = data.calculationCreated ?? false
  form.calculationCreatedAt = data.calculationCreatedAt ?? null

  form.billingReceived = data.billingReceived ?? false
  form.billingReceivedAt = data.billingReceivedAt ?? null

  form.billingCompleted = data.billingCompleted ?? false
  form.billingCompletedAt = data.billingCompletedAt ?? null
}


// submit
const submitForm = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    form.coachingId = +coachingId;

    await portalService.saveInvoiceDocuments(form);

    await getDocumentsData();
    SuccessToast(t('general.toasts.save_success'));
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }
}

onMounted(async () => {
  await Promise.all([
    geCoachingData(),
    getDocumentsData(),
  ]);

  isLoaded.value = true;
});
</script>
<style scoped>

</style>